// A small, secondary section inviting organizations to bring the Build Lab
// in-house. Deliberately compact — half-width card, no Stripe CTA, just a
// clean link to the /organization-inquiry form. Sits between LeadCapture
// and FAQ so it appears late in the scroll (not competing with the main $249 CTA).
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Building2 } from "lucide-react";

export default function ForOrganizations() {
  return (
    <section
      id="for-organizations"
      data-testid="for-organizations-section"
      className="relative py-20 lg:py-24 border-t border-[var(--line)] bg-white"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4 !text-brand">For teams &amp; organizations</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] tracking-[-0.03em] text-ink">
              Want to bring the Build Lab{" "}
              <span className="font-serif-em italic text-brand font-light">
                in-house?
              </span>
            </h2>
            <p className="mt-5 text-[16px] leading-[1.65] text-ink/70 max-w-md">
              Companies, nonprofits, and teams book a custom Build Lab for their staff &mdash;
              a private live session tailored to your org&rsquo;s actual tools, workflows, and goals.
            </p>
            <p className="mt-3 text-sm text-ink/55">
              Not $249 &mdash; scoped &amp; quoted based on group size and objectives.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div
              className="relative rounded-3xl border border-[var(--line)] bg-bone p-8 lg:p-10 overflow-hidden"
              data-testid="for-organizations-card"
            >
              <div className="grid sm:grid-cols-2 gap-6 mb-8">
                <FeatureRow
                  icon={<Users size={16} className="text-brand" />}
                  title="Private group session"
                  body="Just your team. Content shaped around what you actually build."
                />
                <FeatureRow
                  icon={<Building2 size={16} className="text-brand" />}
                  title="Org-specific outcomes"
                  body="Intake systems, dashboards, internal tools &mdash; whatever your org needs to ship."
                />
              </div>

              <div className="hairline my-6" />

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-sm text-ink/70 max-w-md">
                  Tell me about your team and I&rsquo;ll send a proposal within 2 business days.
                </p>
                <Link to="/organization-inquiry" data-testid="for-organizations-cta">
                  <button className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full btn-outline-ink font-medium text-sm whitespace-nowrap group">
                    Request a proposal
                    <ArrowUpRight
                      size={15}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </button>
                </Link>
              </div>

              <div className="mt-6 pt-5 border-t border-[var(--line)] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <p className="text-xs text-ink/55">
                  Serving middle or high school students?{" "}
                  <span className="text-ink/70">
                    The <strong className="text-ink">AI Build Lab: Student Innovation and Entrepreneurship Camp</strong>{" "}
                    is a six-week, in-person after-school program.
                  </span>
                </p>
                <Link
                  to="/student-camp"
                  data-testid="for-organizations-student-camp-link"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline whitespace-nowrap"
                >
                  Learn about the Student Camp
                  <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureRow({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-white border border-[var(--line)] flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div>
        <div className="font-medium text-ink text-[15px] leading-snug">{title}</div>
        <p className="text-sm text-ink/60 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
